import { Node, nodeInputRule } from '@tiptap/core';

export interface VideoOptions {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  HTMLAttributes: Record<string, unknown>;
  allowBase64: boolean;
}

declare module '@tiptap/core' {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Commands<ReturnType> {
    video: {
      /**
       * Insert a video element
       */
      setVideo: (options: { src: string; alt?: string; title?: string; width?: number; height?: number }) => ReturnType;
      /**
       * Set video attributes
       */
      updateVideo: (options: {
        src?: string;
        alt?: string;
        title?: string;
        width?: number;
        height?: number;
      }) => ReturnType;
    };
  }
}

/**
 * Extension TipTap pour la gestion des vidéos
 * Basée sur @sereneinserenade/tiptap-extension-video
 * Adaptée pour le projet Storiz
 */
export const videoExtension = Node.create<VideoOptions>({
  name: 'video',

  addOptions() {
    return {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      HTMLAttributes: {
        class: 'tiptap-video',
        controls: true,
        style: 'max-width: 100%; height: auto; width: 100%;',
      },
      allowBase64: false,
    };
  },

  group: 'block',

  content: '',

  draggable: true,

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element: HTMLElement): string | null => element.getAttribute('src'),
        renderHTML: (attributes: Record<string, unknown>): Record<string, unknown> => ({
          src: attributes.src,
        }),
      },
      alt: {
        default: null,
        parseHTML: (element: HTMLElement): string | null => element.getAttribute('alt'),
        renderHTML: (attributes: Record<string, unknown>): Record<string, unknown> => ({
          alt: attributes.alt,
        }),
      },
      title: {
        default: null,
        parseHTML: (element: HTMLElement): string | null => element.getAttribute('title'),
        renderHTML: (attributes: Record<string, unknown>): Record<string, unknown> => ({
          title: attributes.title,
        }),
      },
      width: {
        default: null,
        parseHTML: (element: HTMLElement): string | null => element.getAttribute('width'),
        renderHTML: (attributes: Record<string, unknown>): Record<string, unknown> => {
          if (!attributes.width) {
            return {};
          }
          return {
            width: attributes.width,
          };
        },
      },
      height: {
        default: null,
        parseHTML: (element: HTMLElement): string | null => element.getAttribute('height'),
        renderHTML: (attributes: Record<string, unknown>): Record<string, unknown> => {
          if (!attributes.height) {
            return {};
          }
          return {
            height: attributes.height,
          };
        },
      },
      controls: {
        default: true,
        parseHTML: (element: HTMLElement): boolean => element.hasAttribute('controls'),
        renderHTML: (attributes: Record<string, unknown>): Record<string, unknown> => {
          if (!attributes.controls) {
            return {};
          }
          return {
            controls: 'controls',
          };
        },
      },

      preload: {
        default: 'metadata',
        parseHTML: (element: HTMLElement): string => element.getAttribute('preload') || 'metadata',
        renderHTML: (attributes: Record<string, unknown>): Record<string, unknown> => ({
          preload: attributes.preload,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'video[src]',
      },
    ];
  },

  // eslint-disable-next-line @typescript-eslint/naming-convention
  renderHTML({ HTMLAttributes }) {
    return ['video', { ...this.options.HTMLAttributes, ...HTMLAttributes }];
  },

  addCommands() {
    return {
      setVideo:
        options =>
        ({ commands }): boolean =>
          commands.insertContent({
            type: this.name,
            attrs: {
              src: options.src,
              alt: options.alt,
              title: options.title,
              width: options.width,
              height: options.height,
            },
          }),

      updateVideo:
        options =>
        ({ commands }): boolean =>
          commands.updateAttributes(this.name, options),
    };
  },

  addInputRules() {
    return [
      nodeInputRule({
        find: /!\[(.+|:?)]\((\S+)(?:(?:\s+)["'](\S+)["'])?\)/,
        type: this.type,
        getAttributes: match => {
          const [, alt, src, title] = match;
          return { src, alt, title };
        },
      }),
    ];
  },
});

export default videoExtension;
